/** Le regole dei chip della lista ordini: quale chip pesca quale ordine.
 *  Stavano in OrdiniPage.tsx; spostate qui il 2026-10-27 quando i chip hanno
 *  preso i numeri (``passaChip``), per non far crescere ancora la pagina. */
import { isBonificoDaVerificare } from "./components/BonificoButton";
import type { Order, PaymentState } from "./types";

// I chip filtro lavorano sul `paymentState` derivato server-side (replica
// la logica del legacy poleepo manager: ordine pagato = mail FATTURA ricevuta;
// ordinato non pagato = mail conferma ricevuta ma fattura ancora no; ecc.).
// Nel legacy "Da evadere" mostra gli ordini Poleepo aperti dei fornitori
// operativi, inclusi quelli ancora da ordinare o con pagamento fornitore
// pendente: Davide li deve vedere per completarli, non perderli dal default.
// Esportata per il test di copertura (ogni PaymentState deve essere pescato da
// almeno un chip — nessun ordine irraggiungibile).
export const STATE_CHIP_TO_PAYMENT: Record<string, PaymentState[]> = {
  // Audit 2026-05-12: "da_evadere" NON include piu' awaiting_payment.
  // Ordini in attesa di pagamento cliente (WAITING_FOR_CUSTOMER_CONFIRM
  // su Poleepo) non sono da evadere: il fornitore non va notificato
  // finche' il cliente non conferma il pagamento. Bug ordine
  // 403-8264344-9899515 (AS, WAITING_FOR_CUSTOMER_CONFIRM): prima
  // appariva in da_evadere via SKU AllService.
  da_evadere: ["paid", "ordered_unpaid", "unpaid_2days", "not_ordered", "as_sent", "as_to_send"],
  spediti: ["shipped"],
  annullati: ["cancelled"],
  // pronta_spedizione gestito separatamente (predicato pickupReady):
  // niente paymentState, mappa vuota. Vedi `STATE_CHIP_PREDICATES`.
  pronta_spedizione: [],
  attesa_pagamento: ["awaiting_payment"],
  // rimborsi (item 20): nessun paymentState (REFUND non è uno stato nostro) —
  // il match è il flag hasReturn, in OR col gruppo (STATE_CHIP_OR_PREDICATES).
  rimborsi: [],
  // Riga PAGAMENTO/INVIO consolidata (F1 2026-07-14). Parte paymentState di
  // ciascun chip; i chip "misti" ORano anche un flag fornitore, vedi
  // STATE_CHIP_OR_PREDICATES. Funzionano in OR con i macro (stessa logica).
  // Vuoto apposta: decide tutto il predicato OR, che guarda PRIMA lo stato
  // d'invio. La chiave resta, o il chip da solo non conterebbe come attivo.
  s_not_ordered: [],
  s_sent: ["as_sent"], // + supplierSendState==='sent'
  s_unpaid: ["ordered_unpaid", "unpaid_2days"], // + supplierUnpaid (include spediti, item 13)
  s_paid: ["paid"],
  s_awaiting_payment: ["awaiting_payment"], // 'Da incassare' + customerUnpaid (include spediti)
};
// Predicati OR dei chip "misti" (F1 item 20): ORano un flag fornitore alla
// lista paymentState del chip (STATE_CHIP_TO_PAYMENT). Un ordine passa il chip
// se il suo paymentState e' ammesso OPPURE il flag matcha — cosi' "Da pagare"
// pesca anche gli ordini spediti col fornitore ancora da pagare (item 13) e
// "Non ordinato"/"Inviato" valgono per QUALSIASI fornitore con flusso d'invio.
export const STATE_CHIP_OR_PREDICATES: Record<string, (o: Order) => boolean> = {
  // Bonifico Woo "da verificare" (paymentState awaiting_payment): l'audit
  // 2026-05-12 tolse awaiting_payment da da_evadere per escludere i marketplace
  // in attesa conferma cliente, ma cosi' sparivano anche i bonifici Woo che
  // Davide DEVE vedere per confermare l'incasso. Li reintroduce SOLO per Woo
  // (isBonificoDaVerificare e' Woo-only), senza riportare gli altri
  // awaiting_payment dei marketplace (item 7).
  da_evadere: (o) => isBonificoDaVerificare(o),
  // ⛔ Chi ha un flusso d'invio lo dice lo stato d'invio, come il badge.
  // ``not_ordered`` vuol dire solo «nessun numero d'ordine fornitore», e chi
  // si ordina via mail o FTP (Cosma, Web Furniture, Aurora) quel numero non lo
  // riceve mai: in OR col paymentState restava «Non ordinato» anche a ordine
  // partito — 7 ordini in produzione il 15/09/2026. Il paymentState decide
  // solo dove uno stato d'invio non esiste (magazzino, marketplace diretto).
  s_not_ordered: (o) =>
    o.supplierSendState != null
      ? o.supplierSendState === "to_send"
      : o.paymentState === "not_ordered" || o.paymentState === "as_to_send",
  s_sent: (o) => o.supplierSendState === "sent",
  s_unpaid: (o) => o.supplierUnpaid === true,
  // 'Da incassare' pesca anche gli ordini col paymentState collassato
  // (es. spedito) ma cliente ancora da incassare (item 13, lato cliente).
  s_awaiting_payment: (o) => o.customerUnpaid === true,
  // 'Rimborsi': reso/sostituzione in corso o chiuso (rimborso in corso o già
  // rimborsato). Non gatato dal fornitore (non è un chip pagamento-fornitore).
  rimborsi: (o) => o.hasReturn === true,
};
// I filtri pagamento fornitore "Pagato"/"Da pagare" valgono SOLO per Itamoby/
// Ityhome — come i badge pagamento (``shouldShowPaymentBadge``): per gli altri
// fornitori il concetto "pagato/non pagato fornitore" non esiste, quindi i loro
// ordini NON devono comparire (richiesta Davide 2026-06-08). "Non ordinato"/
// "Inviato" NON sono in questo gate (ora cross-fornitore). NB: gli ordini con
// supplierUnpaid restano sempre visibili (item 13, guardia nel filtro).
export const SUPPLIER_PAYMENT_CHIP_IDS = ["s_paid", "s_unpaid"];
export const SUPPLIER_PAYMENT_STATES = new Set<PaymentState>(
  SUPPLIER_PAYMENT_CHIP_IDS.flatMap((id) => STATE_CHIP_TO_PAYMENT[id] ?? []),
);
export const PAYMENT_FILTER_SUPPLIERS = new Set(["itamoby", "ityhome"]);
// Il filtro fornitore non e' piu' a chip ma un elenco a discesa di sola
// visualizzazione (richiesta Davide). I 4 fornitori gestiti sono mappati
// 1:1 su ``Order.fornitore.kind`` (cosma derivato server-side per
// appartenenza al catalogo SKU). Vedi ``OrdiniFilterCard``.

// Predicati custom per chip che non mappano direttamente su paymentState.
// pronta_spedizione: parity legacy _row_has_spedizione_pronta (tab_ordini.py:
// 3599-3607: if mail_ldv_inviata: return False → poi controlla merce_pronta).
// Mostra solo ordini PRONTI AL RITIRO la cui mail LDV NON è ancora stata
// inviata (pickupMailSent=false/undef). Ordini con LDV già inviata escono
// dal filtro: Davide li ha già gestiti.
// Deviazione legacy VOLUTA (decisione Davide 2026-05-19, vault
// 20-Business/Decisions): "Spedizione Pronta" deve mostrare TUTTI gli
// ordini la cui email "Merce pronta al ritiro" e' arrivata OGGI
// (merceProntaToday, computed server-side), ANCHE se gia' spediti o con
// LDV gia' inviata — Davide vuole vedere gli ordini delle mail di oggi
// (caso reale ITYH-80203/80204 gia' FULL_SHIPPED). La condizione legacy
// (pickup pronto, LDV non ancora inviata) resta in OR per non perdere gli
// ordini pronti di giorni precedenti non ancora gestiti.
// pickupMailMissing (fix 2026-07-16, ordine 2001702672-A): gli ordini gia'
// spediti con mail di spedizione MAI partita rientrano nel chip — erano
// invisibili ovunque (pickupReady esclude gli spediti, merceProntaToday
// copre solo oggi) e la merce restava ferma dal fornitore per giorni.
// Esportata per i test (verifica del predicato senza render completo).
export const STATE_CHIP_PREDICATES: Record<string, (o: Order) => boolean> = {
  pronta_spedizione: (o) =>
    o.merceProntaToday === true ||
    o.pickupMailMissing === true ||
    (o.pickupReady === true && o.pickupMailSent !== true),
};

/** Un ordine passerebbe QUESTO chip acceso da solo? Serve ai numeri sui chip
 *  (redesign lista 2026-10-27): dicono quanti ordini ci sono in ogni coda prima
 *  di cliccarla. Stessa regola del filtro vero: paymentState o predicato OR,
 *  predicato AND per «Spedizione pronta», gate Itamoby/Ityhome per
 *  «Pagato»/«Da pagare». ``ordiniPage.chip.test`` la confronta col filtro. */
export function passaChip(o: Order, id: string): boolean {
  const predicato = STATE_CHIP_PREDICATES[id];
  if (predicato) return predicato(o);
  const ammessi = STATE_CHIP_TO_PAYMENT[id] ?? [];
  const oppure = STATE_CHIP_OR_PREDICATES[id];
  if (!ammessi.includes(o.paymentState) && !(oppure?.(o) ?? false)) return false;
  return !(
    SUPPLIER_PAYMENT_CHIP_IDS.includes(id) &&
    SUPPLIER_PAYMENT_STATES.has(o.paymentState) &&
    !PAYMENT_FILTER_SUPPLIERS.has(o.fornitore.kind) &&
    o.supplierUnpaid !== true
  );
}
